import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useParams } from 'react-router-dom';

import '../../assets/css/admin-videos-add.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

function buildUrl(path) {
  return `${API_BASE.replace(/\/$/, '')}${path}`;
}

function imageSrc(path) {
  if (!path) return '';
  const p = String(path).trim();
  if (typeof window !== 'undefined' && p.startsWith('/uploads/')) {
    return window.location.origin + (p.startsWith('/') ? p : '/' + p);
  }
  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  if (typeof window !== 'undefined') {
    return window.location.origin + (p.startsWith('/') ? p : '/' + p);
  }
  return p;
}

const VIDEO_CATEGORIES = [
  { slug: 'travel-with-dinesh', label: 'Travel with dinesh' },
  { slug: 'ape-pansala', label: 'Ape pansala' },
  { slug: 'urumaya', label: 'Urumaya' },
  { slug: 'ranga-rata', label: 'Ranga Rata' },
];

function AdminVideos() {
  const { categorySlug } = useParams();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formFile, setFormFile] = useState(null);
  const [formThumbnail, setFormThumbnail] = useState(null);
  const [formCategory, setFormCategory] = useState(VIDEO_CATEGORIES[0].slug);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editCategory, setEditCategory] = useState(VIDEO_CATEGORIES[0].slug);
  const [editUrl, setEditUrl] = useState('');
  const [editVideoFile, setEditVideoFile] = useState(null);
  const [editThumbnailFile, setEditThumbnailFile] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');

  const editVideoObjectUrl = useMemo(() => {
    if (!editVideoFile) return null;
    return URL.createObjectURL(editVideoFile);
  }, [editVideoFile]);

  useEffect(() => () => {
    if (editVideoObjectUrl) URL.revokeObjectURL(editVideoObjectUrl);
  }, [editVideoObjectUrl]);

  const editThumbnailObjectUrl = useMemo(() => {
    if (!editThumbnailFile) return null;
    return URL.createObjectURL(editThumbnailFile);
  }, [editThumbnailFile]);

  useEffect(() => () => {
    if (editThumbnailObjectUrl) URL.revokeObjectURL(editThumbnailObjectUrl);
  }, [editThumbnailObjectUrl]);

  const currentCategory = categorySlug
    ? VIDEO_CATEGORIES.find((c) => c.slug === categorySlug)
    : null;
  const pageTitle = currentCategory ? currentCategory.label : 'All videos';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const query = categorySlug && VIDEO_CATEGORIES.some((c) => c.slug === categorySlug)
      ? `?category=${encodeURIComponent(categorySlug)}`
      : '';
    fetch(buildUrl(`/api/videos${query}`))
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.success) setVideos(data.videos || []);
      })
      .catch(() => {
        if (!cancelled) setVideos([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [categorySlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const title = formTitle.trim();
    const category = formCategory.trim();
    const catSlug = VIDEO_CATEGORIES.find((c) => c.slug === category) ? category : VIDEO_CATEGORIES[0].slug;
    if (!title) {
      setError('Title is required.');
      return;
    }
    if (!formFile) {
      setError('Upload a video file.');
      return;
    }
    setSubmitting(true);
    try {
      // Always use multipart/form-data so the backend multer middleware
      // can parse the request consistently (even for URL-only submissions).
      const formData = new FormData();
      formData.append('title', title);
      formData.append('category', catSlug);
      if (formFile) formData.append('video', formFile);
      if (formThumbnail) formData.append('thumbnail', formThumbnail);

      const res = await fetch(buildUrl('/api/videos'), {
        method: 'POST',
        body: formData,
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        const newVideo = data.video ? { ...data.video } : {};
        if (formThumbnail) {
          try {
            newVideo.thumbnailPreview = URL.createObjectURL(formThumbnail);
          } catch (_e) {
            // ignore preview failure
          }
        }
        setFormTitle('');
        setFormFile(null);
        setFormThumbnail(null);
        setVideos((prev) => [newVideo, ...prev]);
        setShowAddForm(false);
      } else {
        const msg = data && data.message ? data.message : `Failed to add video (${res.status}).`;
        setError(msg);
      }
    } catch (err) {
      setError(err && err.message ? err.message : 'Cannot reach server.');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (video) => {
    setEditingVideo(video);
    setEditTitle(video.title || '');
    setEditCategory(
      VIDEO_CATEGORIES.some((c) => c.slug === video.category)
        ? video.category
        : VIDEO_CATEGORIES[0].slug,
    );
    const uploaded = (video.url || '').startsWith('/uploads/videos/');
    setEditUrl(uploaded ? '' : (video.url || ''));
    setEditVideoFile(null);
    setEditThumbnailFile(null);
    setEditError('');
  };

  const closeEdit = () => {
    setEditingVideo(null);
    setEditTitle('');
    setEditCategory(VIDEO_CATEGORIES[0].slug);
    setEditUrl('');
    setEditVideoFile(null);
    setEditThumbnailFile(null);
    setEditError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingVideo) return;
    setEditError('');
    const title = editTitle.trim();
    if (!title) {
      setEditError('Title is required.');
      return;
    }
    const isUploaded = (editingVideo.url || '').startsWith('/uploads/videos/');
    if (!isUploaded && !editUrl.trim()) {
      setEditError('Video URL is required for external videos.');
      return;
    }
    setEditSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('category', editCategory);
      if (editVideoFile) formData.append('video', editVideoFile);
      if (editThumbnailFile) formData.append('thumbnail', editThumbnailFile);
      if (!isUploaded && editUrl.trim()) {
        formData.append('url', editUrl.trim());
      }
      const res = await fetch(buildUrl(`/api/videos/${editingVideo._id}`), {
        method: 'PUT',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.video) {
        const next = { ...data.video };
        delete next.thumbnailPreview;
        setVideos((prev) => prev.map((v) => (v._id === next._id ? next : v)));
        closeEdit();
        setPlayingId(null);
      } else {
        setEditError(data.message || `Failed to update (${res.status}).`);
      }
    } catch (err) {
      setEditError(err && err.message ? err.message : 'Cannot reach server.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async () => {
    const id = deleteConfirmId;
    if (!id) return;
    setDeleteConfirmId(null);
    setDeletingId(id);
    try {
      const res = await fetch(buildUrl(`/api/videos/${id}`), { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setVideos((prev) => prev.filter((v) => v._id !== id));
      }
    } finally {
      setDeletingId(null);
    }
  };

  const categoryLabel = (slug) => VIDEO_CATEGORIES.find((c) => c.slug === slug)?.label || slug;

  return (
    <div className="admin-dashboard">
      <h1 className="admin-dashboard-title">Videos</h1>

      <div className="admin-videos-nav">
        <NavLink
          to="/admin/videos"
          className={({ isActive }) => `admin-videos-nav-link ${isActive ? 'active' : ''}`}
          end
        >
          All videos
        </NavLink>
        {VIDEO_CATEGORIES.map((cat) => (
          <NavLink
            key={cat.slug}
            to={`/admin/videos/${cat.slug}`}
            className={({ isActive }) => `admin-videos-nav-link ${isActive ? 'active' : ''}`}
          >
            {cat.label}
          </NavLink>
        ))}
      </div>

      <div className="admin-dashboard-section">
        <h2 className="admin-section-title">{pageTitle}</h2>

        {!showAddForm ? (
          <button
            type="button"
            className="admin-video-add-toggle-btn"
            onClick={() => setShowAddForm(true)}
          >
            <i className="far fa-plus" /> Add video
          </button>
        ) : (
          <div className="admin-video-form-wrap">
            <div className="admin-video-form-header">
              <h3 className="admin-video-form-title">Add video</h3>
              <button
                type="button"
                className="admin-video-form-close"
                onClick={() => { setShowAddForm(false); setError(''); }}
                aria-label="Close form"
              >
                <i className="far fa-times" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="admin-video-form">
              {error && (
                <div className="admin-auth-error" role="alert">
                  {error}
                </div>
              )}
              <div className="admin-video-form-row">
                <div className="form-group">
                  <label htmlFor="video-title">Title</label>
                  <input
                    id="video-title"
                    type="text"
                    className="form-control"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    placeholder="Video title"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="video-category">Category</label>
                  <select
                    id="video-category"
                    className="form-control"
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value)}
                  >
                    {VIDEO_CATEGORIES.map((c) => (
                      <option key={c.slug} value={c.slug}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="video-thumbnail">Thumbnail image</label>
                <div className="admin-video-upload-box admin-video-thumbnail-upload-box">
                  <input
                    id="video-thumbnail"
                    key={formThumbnail ? 'has-thumb' : 'no-thumb'}
                    type="file"
                    accept="image/*"
                    className="admin-video-upload-input"
                    onChange={(e) => setFormThumbnail(e.target.files?.[0] || null)}
                  />
                  <span className="admin-video-upload-label admin-video-thumbnail-upload-label">
                    {formThumbnail ? formThumbnail.name : 'Upload thumbnail image'}
                  </span>
                </div>
                <span className="admin-video-thumbnail-hint">
                  Optional: thumbnail is saved with the video
                </span>
              </div>
              <div className="form-group admin-video-upload-group">
                <label htmlFor="video-upload">Upload video file</label>
                <div className="admin-video-upload-box">
                  <input
                    id="video-upload"
                    key={formFile ? 'has-file' : 'no-file'}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-m4v,.mp4,.webm,.mov,.avi,.m4v"
                    className="admin-video-upload-input"
                    onChange={(e) => setFormFile(e.target.files?.[0] || null)}
                  />
                  <span className="admin-video-upload-label">
                    {formFile ? formFile.name : 'Click or drop a video here (mp4, webm, mov, etc.)'}
                  </span>
                </div>
              </div>
              <button type="submit" className="admin-auth-submit" disabled={submitting}>
                {submitting ? 'Adding…' : 'Add video'}
              </button>
            </form>
          </div>
        )}

        <div className="admin-videos-list-wrap">
          {loading ? (
            <p className="admin-section-description">Loading videos…</p>
          ) : videos.length === 0 ? (
            <p className="admin-section-description">No videos yet. Click &quot;Add video&quot; to add one.</p>
          ) : (
            <ul className="admin-videos-list admin-videos-grid">
              {videos.map((video) => {
                const isUploaded = (video.url || '').startsWith('/uploads/videos/');
                const videoSrc = isUploaded && typeof window !== 'undefined'
                  ? window.location.origin + video.url
                  : video.url;
                const thumbSrc = video.thumbnailPreview
                  ? video.thumbnailPreview
                  : imageSrc(
                      video.thumbnail ||
                      video.poster ||
                      video.thumbnailUrl ||
                      video.thumbnail_path ||
                      video.thumb ||
                      video.image ||
                      ''
                    );
                const isPlaying = playingId === video._id;
                return (
                  <li key={video._id} className="admin-video-item">
                    <div className="admin-video-item-main">
                      {isPlaying && isUploaded ? (
                        <div className="admin-video-item-player-wrap">
                          <video
                            className="admin-video-item-player"
                            src={videoSrc}
                            controls
                            autoPlay
                            preload="metadata"
                            playsInline
                            key={video.url}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      ) : (
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            borderRadius: 8,
                            overflow: 'hidden',
                            background: '#000',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (isUploaded) {
                              setPlayingId(video._id);
                            } else if (video.url) {
                              window.open(video.url, '_blank', 'noopener');
                            }
                          }}
                        >
                          {thumbSrc ? (
                            <img
                              src={thumbSrc}
                              alt={video.title}
                              style={{
                                width: '100%',
                                display: 'block',
                                objectFit: 'cover',
                                height: 190,
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: '100%',
                                height: 190,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: 13,
                                opacity: 0.75,
                              }}
                            >
                              {isUploaded ? 'Click to play video' : 'Click to open video'}
                            </div>
                          )}
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'radial-gradient(circle at center, rgba(0,0,0,0.1), rgba(0,0,0,0.55))',
                              pointerEvents: 'none',
                            }}
                          >
                            <div
                              style={{
                                width: 52,
                                height: 52,
                                borderRadius: '50%',
                                background: 'rgba(0,0,0,0.6)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
                              }}
                            >
                              <i className="far fa-play" style={{ marginLeft: 3, fontSize: 20 }} />
                            </div>
                          </div>
                        </div>
                      )}
                      <span className="admin-video-item-title">{video.title}</span>
                      <span className="admin-video-item-category">{categoryLabel(video.category)}</span>
                      {isUploaded && (
                        <a
                          href={videoSrc}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="admin-video-item-link"
                        >
                          Open in new tab
                        </a>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '0.5rem',
                        width: '100%',
                        padding: '0 0.75rem 0.75rem',
                        boxSizing: 'border-box',
                      }}
                    >
                      <button
                        type="button"
                        className="admin-video-delete-btn"
                        style={{ margin: 0, flexShrink: 0 }}
                        onClick={() => handleDeleteClick(video._id)}
                        disabled={deletingId === video._id || Boolean(editingVideo)}
                        aria-label="Delete video"
                      >
                        {deletingId === video._id ? '…' : 'Delete'}
                      </button>
                      <button
                        type="button"
                        className="admin-video-cancel-btn"
                        style={{ margin: 0, flexShrink: 0 }}
                        onClick={() => openEdit(video)}
                        disabled={Boolean(editingVideo) || Boolean(deleteConfirmId)}
                        aria-label="Edit video"
                      >
                        Edit
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {editingVideo && (() => {
        const editIsUploaded = (editingVideo.url || '').startsWith('/uploads/videos/');
        const editVideoSrc = editIsUploaded && typeof window !== 'undefined'
          ? window.location.origin + editingVideo.url
          : editingVideo.url;
        const editCurrentThumb = imageSrc(
          editingVideo.thumbnail ||
            editingVideo.poster ||
            editingVideo.thumbnailUrl ||
            editingVideo.thumbnail_path ||
            editingVideo.thumb ||
            editingVideo.image ||
            '',
        );
        return (
        <div className="admin-confirm-overlay" onClick={closeEdit} role="dialog" aria-modal="true" aria-labelledby="admin-video-edit-title">
          <div className="admin-confirm-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <h3 id="admin-video-edit-title" className="admin-confirm-title">Edit video</h3>
            <form onSubmit={handleEditSubmit} className="admin-video-form" style={{ marginTop: '0.75rem' }}>
              {editError && (
                <div className="admin-auth-error" role="alert">
                  {editError}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="video-edit-title">Title</label>
                <input
                  id="video-edit-title"
                  type="text"
                  className="form-control"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="Video title"
                />
              </div>
              <div className="form-group">
                <label htmlFor="video-edit-category">Category</label>
                <select
                  id="video-edit-category"
                  className="form-control"
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                >
                  {VIDEO_CATEGORIES.map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              {(editingVideo.url || '').startsWith('/uploads/videos/') ? null : (
                <div className="form-group">
                  <label htmlFor="video-edit-url">Video URL</label>
                  <input
                    id="video-edit-url"
                    type="url"
                    className="form-control"
                    value={editUrl}
                    onChange={(e) => setEditUrl(e.target.value)}
                    placeholder="https://…"
                  />
                </div>
              )}
              <div className="form-group admin-video-upload-group" style={{ marginBottom: 0 }}>
                <label htmlFor="video-edit-file">
                  {(editingVideo.url || '').startsWith('/uploads/videos/')
                    ? 'Replace video file (optional)'
                    : 'Upload hosted video instead (optional)'}
                </label>
                <div className="admin-video-upload-box">
                  <input
                    id="video-edit-file"
                    key={editVideoFile ? 'edit-has-video' : 'edit-no-video'}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-m4v,.mp4,.webm,.mov,.avi,.m4v"
                    className="admin-video-upload-input"
                    onChange={(e) => setEditVideoFile(e.target.files?.[0] || null)}
                  />
                  <span className="admin-video-upload-label">
                    {editVideoFile ? editVideoFile.name : 'No file chosen'}
                  </span>
                </div>
              </div>
              <div
                style={{
                  borderTop: '1px solid #e5e7eb',
                  marginTop: '0.75rem',
                  paddingTop: '0.75rem',
                }}
              >
                <p style={{ fontSize: '0.8rem', fontWeight: 600, margin: '0 0 0.5rem', color: '#374151' }}>
                  {editVideoObjectUrl ? 'New video preview' : 'Current video'}
                </p>
                <div
                  style={{
                    padding: '0.65rem',
                    background: '#fafafa',
                    borderRadius: 6,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  {editVideoObjectUrl ? (
                    <video
                      src={editVideoObjectUrl}
                      controls
                      muted
                      playsInline
                      preload="metadata"
                      style={{
                        width: '100%',
                        maxHeight: 200,
                        borderRadius: 6,
                        background: '#000',
                        display: 'block',
                      }}
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : editIsUploaded ? (
                    <video
                      src={editVideoSrc}
                      controls
                      muted
                      playsInline
                      preload="metadata"
                      style={{
                        width: '100%',
                        maxHeight: 200,
                        borderRadius: 6,
                        background: '#000',
                        display: 'block',
                      }}
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <div style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                      <span style={{ color: '#6b7280', display: 'block', marginBottom: 4 }}>External link</span>
                      <a href={editUrl || editingVideo.url} target="_blank" rel="noopener noreferrer">
                        {editUrl || editingVideo.url || '—'}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group admin-video-upload-group" style={{ marginTop: '1.25rem', marginBottom: 0 }}>
                <label htmlFor="video-edit-thumbnail">Replace thumbnail (optional)</label>
                <div className="admin-video-upload-box admin-video-thumbnail-upload-box">
                  <input
                    id="video-edit-thumbnail"
                    key={editThumbnailFile ? 'edit-has-thumb' : 'edit-no-thumb'}
                    type="file"
                    accept="image/*"
                    className="admin-video-upload-input"
                    onChange={(e) => setEditThumbnailFile(e.target.files?.[0] || null)}
                  />
                  <span className="admin-video-upload-label admin-video-thumbnail-upload-label">
                    {editThumbnailFile ? editThumbnailFile.name : 'Keep current thumbnail or upload new'}
                  </span>
                </div>
              </div>
              <div
                style={{
                  borderTop: '1px solid #e5e7eb',
                  marginTop: '0.75rem',
                  paddingTop: '0.75rem',
                }}
              >
                <p style={{ fontSize: '0.8rem', fontWeight: 600, margin: '0 0 0.5rem', color: '#374151' }}>
                  {editThumbnailObjectUrl ? 'New thumbnail preview' : 'Current thumbnail'}
                </p>
                <div
                  style={{
                    padding: '0.65rem',
                    background: '#fafafa',
                    borderRadius: 6,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  {editThumbnailObjectUrl || editCurrentThumb ? (
                    <img
                      src={editThumbnailObjectUrl || editCurrentThumb}
                      alt=""
                      style={{
                        maxWidth: '100%',
                        maxHeight: 160,
                        objectFit: 'cover',
                        borderRadius: 6,
                        display: 'block',
                      }}
                    />
                  ) : (
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>No thumbnail</p>
                  )}
                </div>
              </div>
              <div className="admin-confirm-actions" style={{ marginTop: '1rem', justifyContent: 'flex-end' }}>
                <button type="button" className="admin-confirm-cancel" onClick={closeEdit}>
                  Cancel
                </button>
                <button type="submit" className="admin-auth-submit" style={{ width: 'auto', marginTop: 0 }} disabled={editSubmitting}>
                  {editSubmitting ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
        );
      })()}

      {deleteConfirmId && (
        <div className="admin-confirm-overlay" onClick={() => setDeleteConfirmId(null)} role="dialog" aria-modal="true" aria-labelledby="admin-confirm-title">
          <div className="admin-confirm-box" onClick={(e) => e.stopPropagation()}>
            <h3 id="admin-confirm-title" className="admin-confirm-title">Delete video?</h3>
            <p className="admin-confirm-text">This cannot be undone.</p>
            <div className="admin-confirm-actions">
              <button type="button" className="admin-confirm-cancel" onClick={() => setDeleteConfirmId(null)}>Cancel</button>
              <button type="button" className="admin-confirm-delete" onClick={handleDeleteConfirm}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminVideos;
